import { del, get, patch, post } from './http';

export type LinkedResourceType = 'solution' | 'tool';

export interface FileLinkedTarget {
  type: LinkedResourceType;
  id: string;
  title: string;
}

export interface UploadedFileItem {
  id: number;
  fileObjectId: number;
  name: string;
  originalName?: string;
  size: string;
  sizeBytes: number;
  date: string;
  type: string;
  category: string;
  objectKey: string;
  mimeType?: string | null;
  objectStatus: 'active' | 'recycled' | 'deleted';
  recycledAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  downloadUrl: string;
  linkedTo?: FileLinkedTarget | null;
}

export interface FileListResponse {
  total: number;
  page: number;
  pageSize: number;
  list: UploadedFileItem[];
}

export interface FileStatsDistribution {
  label: string;
  bytes: number;
  usedPct: number;
}

export interface FileStatsResponse {
  activeCount: number;
  recycledCount: number;
  deletedCount: number;
  activeBytes: number;
  activeSizeText: string;
  distribution: FileStatsDistribution[];
}

export interface PresignUploadResponse {
  uploadUrl: string;
  objectKey: string;
  bucket: string;
  endpoint: string;
}

export interface CompleteUploadResponse {
  fileObjectId: number;
  uploadedFileId: number;
  objectKey: string;
  displayName: string;
}

export function presignUploadFile(payload: {
  fileName: string;
  contentType: string;
  fileCategory: 'avatar' | 'solution_attachment' | 'tool_attachment' | 'other';
}) {
  return post<PresignUploadResponse>('/files/presign-upload', payload);
}

export function completeUploadFile(payload: {
  objectKey: string;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  fileCategory: 'avatar' | 'solution_attachment' | 'tool_attachment' | 'other';
  linkedResourceType?: LinkedResourceType;
  linkedResourceId?: number;
}) {
  return post<CompleteUploadResponse>('/files/complete', payload);
}

export function getAdminFiles(params?: {
  page?: number;
  pageSize?: number;
  search?: string;
  fileCategory?: string;
  linkedType?: 'all' | 'solution' | 'tool' | 'unlinked';
}) {
  return get<FileListResponse>('/admin/files', { params });
}

export function getAdminRecycleFiles(params?: {
  page?: number;
  pageSize?: number;
  search?: string;
  fileCategory?: string;
  linkedType?: 'all' | 'solution' | 'tool' | 'unlinked';
}) {
  return get<FileListResponse>('/admin/files/recycle', { params });
}

export function getAdminFileStats() {
  return get<FileStatsResponse>('/admin/files/stats');
}

export function updateAdminFileLink(
  id: string | number,
  payload: {
    linkedResourceType?: LinkedResourceType | null;
    linkedResourceId?: number | null;
  },
) {
  return patch<UploadedFileItem>(`/admin/files/${id}/link`, payload);
}

export function recycleAdminFile(id: string | number) {
  return del<{ id: number }>('/admin/files/' + id);
}

export function restoreAdminFile(id: string | number) {
  return post<{ id: number }>(`/admin/files/${id}/restore`);
}

export function permanentlyDeleteAdminFile(id: string | number) {
  return del<{ id: number }>(`/admin/files/${id}/permanent`);
}
